import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import {
  PagerLoadMoreVariant,
  PagerPosition,
  PagerVariant
} from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import userSearchQuery from '@aurora/shared-client/components/users/UserSearch.query.graphql';
import type { UserSearchConnection } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import React, { useEffect, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, UserViewSize, UserViewVariant } from '../../../types/enums';
import type {
  UserSearchQuery,
  UserSearchQueryVariables,
  UserSearchViewFragment
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import type { ItemViewTypeAndProps, ItemViewVariantProps } from '../../entities/types';
import { SearchItemType } from '../../search/enums';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import type { UserListForSearchTermProps } from '../types';
import localStyles from './UserListForSearchTerm.module.pcss';

/**
 *  Displays a list of Users for a given search term.
 *
 *  @author Deepak Yadav
 */
const UserListForSearchTerm: React.FC<React.PropsWithChildren<UserListForSearchTermProps>> = ({
  onCompleted,
  queryTerm,
  listVariant,
  userViewVariantPropsOverrides = {},
  pagerVariant = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START,
      variant: PagerLoadMoreVariant.BOXED
    }
  },
  pageSize = 5,
  paneledListClassname,
  headerClassname,
  footerClassname,
  panel = PanelType.NONE,
  useEmpty = false,
  constrainPages,
  onRendered,
  useHeader
}: UserListForSearchTermProps) => {
  const currentPageNumber = useRef(0);
  const pageCount = useRef(0);
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_LIST_FOR_SEARCH_TERM
  );
  const trimmedTerm: string = queryTerm?.trim() ?? '';

  const userViewVariantDefaultProps: ItemViewVariantProps<ItemType.USER, UserViewVariant.INLINE> = {
    useAvatar: true,
    useRank: true,
    viewSize: UserViewSize.SM,
    useHoverCard: false
  };

  const userViewVariant: ItemViewTypeAndProps<ItemType.USER, UserViewVariant.INLINE> = {
    type: UserViewVariant.INLINE,
    props: {
      ...userViewVariantDefaultProps,
      ...userViewVariantPropsOverrides
    }
  };

  const queryResult = useEntityViewQuery<
    ItemType.USER_SEARCH,
    UserSearchQuery,
    UserSearchQueryVariables
  >(module, ItemType.USER_SEARCH, userViewVariant, userSearchQuery, {
    variables: {
      searchTerm: trimmedTerm,
      first: pageSize
    },
    onCompleted,
    onError: React.useCallback(() => {
      onCompleted(null);
    }, [onCompleted]),
    notifyOnNetworkStatusChange: true,
    skip: trimmedTerm.length < 2
  });

  useEffect(() => {
    if (onRendered && currentPageNumber.current !== pageCount.current) {
      pageCount.current = currentPageNumber.current;
      onRendered(currentPageNumber.current, SearchItemType.USER);
    }
  });

  if (textLoading) {
    return null;
  }

  /**
   * Callback after pagination has occurred
   * @param connection
   */
  function onUpdate(connection: UserSearchConnection): UserSearchQuery {
    currentPageNumber.current += 1;
    const { pageInfo } = connection;
    const { hasNextPage } = pageInfo;
    return {
      userSearch: {
        ...connection,
        pageInfo: {
          ...pageInfo,
          hasNextPage: hasNextPage && currentPageNumber.current < constrainPages
        }
      }
    } as UserSearchQuery;
  }

  /**
   * The no result section to be rendered for the PaneledItemList.
   */
  const noResults: React.FC<React.PropsWithChildren<unknown>> = () => (
    <EmptyState
      className={cx('lia-g-mt-10')}
      description={formatMessage('emptyDescription')}
      descriptionClassName={cx('lia-empty-state-description')}
      alignment={TextAlignment.LEFT}
    />
  );

  /**
   * The Header to be rendered for the PaneledItemList.
   *
   * @param key the key to fetch the header text for.
   */
  function renderResultsHeader(key: string): React.FC<React.PropsWithChildren<unknown>> {
    function Header(): React.ReactElement {
      return <h5 className={cx('lia-content-heading')}>{formatMessage(key)}</h5>;
    }
    return Header;
  }

  if (pagerVariant) {
    pagerVariant.props = pagerVariant.props || {};
    pagerVariant.props.ariaLabel = formatMessage('showMoreProfiles');
  }

  return queryResult?.loading || queryResult?.data?.userSearch?.edges?.length > 0 ? (
    <PaneledItemList<
      UserSearchViewFragment,
      ItemType.USER_SEARCH,
      ItemViewTypeAndProps<ItemType.USER_SEARCH, UserViewVariant>,
      UserSearchQuery,
      UserSearchQueryVariables
    >
      className={paneledListClassname}
      type={ItemType.USER_SEARCH}
      variant={userViewVariant}
      queryResult={queryResult}
      listVariant={listVariant}
      pagerVariant={pagerVariant}
      pageSize={pageSize}
      onUpdate={constrainPages && onUpdate}
      loadingSize={null}
      empty={noResults}
      header={useHeader ? renderResultsHeader('users') : null}
      useHeaderWhenEmpty={false}
      panel={panel}
      headerClassName={headerClassname}
      footerClassName={footerClassname}
      useEmpty={useEmpty}
    />
  ) : null;
};

export default UserListForSearchTerm;
